import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';

import {
  LibCoreLayoutModule,
  SuccessCheckAnimationDialogComponent,
  SuccessCheckAnimationDialogComponentData,
} from '../../../ui/core-layout';
import {
  APP_MODE,
  Appointment,
  BusinessObject,
  ResponseError,
} from '../../../models';
import {
  AbstractStorageService,
  DialogService,
  ErrorHandlerServiceService,
} from '../../../services';
import { CalendarAppointmentDetailsComponent } from '../calendar-appointment-details/calendar-appointment-details.component';
import { CalendarStorageService } from '../services/calendar-storage.service';
import { CalendarService } from '../services/calendar.service';

export interface CalendarAppointmentConfirmationDialogComponentData {
  businessObject: BusinessObject;
  appointment: Appointment;
  mode: APP_MODE;
}

export interface CalendarAppointmentConfirmationDialogComponentOutputData {
  isSlotNotAvailable?: boolean;
  createdOrUpdatedAppointment?: Appointment;
}

@Component({
  selector: 'lib-calendar-appointment-confirmation-dialog',
  templateUrl: './calendar-appointment-confirmation-dialog.component.html',
  styleUrls: ['./calendar-appointment-confirmation-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    LibCoreLayoutModule,
    CalendarAppointmentDetailsComponent,
    // material design
    MatButtonModule,
  ],
})
export class CalendarAppointmentConfirmationDialogComponent {
  public mode: APP_MODE;
  public businessObject: BusinessObject;
  public appointment: Appointment;

  public isLoading = false;

  private readonly NOT_AVAILABLE_SLOT_MESSAGE = [
    'No available slot at desired start time',
    'startTime.invalid',
  ];

  constructor(
    protected calendarStorageService: CalendarStorageService,
    @Inject(MAT_DIALOG_DATA)
    private data: CalendarAppointmentConfirmationDialogComponentData,
    private dialogRef: MatDialogRef<CalendarAppointmentConfirmationDialogComponent>,
    private errorHandlerServiceService: ErrorHandlerServiceService,
    private dialogService: DialogService,
    private abstractStorageService: AbstractStorageService,
    private calendarService: CalendarService
  ) {
    this.calendarStorageService.setCalendarMode(this.data?.mode);

    this.mode = this.data?.mode;
    this.businessObject = this.data?.businessObject;
    this.appointment = this.data?.appointment;
  }

  public async submit() {
    if (this.isLoading) {
      return;
    }

    try {
      this.isLoading = true;

      let createdOrUpdatedAppointment: Appointment;

      if (this.appointment?.id) {
        const isUpdateSuccess =
          await this.calendarService.updateAppointmentById({
            ...this.appointment,
            owner: undefined,
          });

        if (isUpdateSuccess) {
          createdOrUpdatedAppointment = { ...this.appointment };
        }
      } else if (this.appointment?.owner?.id) {
        createdOrUpdatedAppointment =
          await this.calendarService.createNewAppointment(
            this.businessObject.id,
            this.appointment?.owner?.id,
            { ...this.appointment, owner: undefined }
          );
      } else {
        createdOrUpdatedAppointment =
          await this.calendarService.createNewAppointmentAsGuest(
            this.businessObject.id,
            this.appointment
          );
      }

      if (createdOrUpdatedAppointment?.id) {
        if (!createdOrUpdatedAppointment?.services?.length) {
          this.abstractStorageService.saveLastSelectedDuration(
            createdOrUpdatedAppointment.estimatedDuration
          );
        }

        this.displaySuccessCheckAnimationDialog();
        this.closeDialog({
          createdOrUpdatedAppointment: createdOrUpdatedAppointment,
        });
      }
    } catch (e: any) {
      this.errorHandlerServiceService.handleError(e);

      const error: ResponseError = e?.error;
      if (
        error?.status === 400 &&
        this.NOT_AVAILABLE_SLOT_MESSAGE.includes(error?.message)
      ) {
        this.closeDialog({
          isSlotNotAvailable: true,
        });
      }
    } finally {
      this.isLoading = false;
    }
  }

  public closeDialog(
    outputData?: CalendarAppointmentConfirmationDialogComponentOutputData
  ): void {
    this.dialogRef.close(outputData || null);
  }

  private displaySuccessCheckAnimationDialog(): void {
    this.dialogService.openDialog(
      SuccessCheckAnimationDialogComponent,
      this.dialogService.createSmallDialogConfig({
        text: this.appointment?.id
          ? 'lib_calendar.appointment-confirmation-dialog.text.update-successfully'
          : 'lib_calendar.appointment-confirmation-dialog.text.book-successfully',
      } as SuccessCheckAnimationDialogComponentData)
    );
  }
}
